/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { CryptoProviderFactory } from "../../device/crypto";
import { Log } from "../../logging/Log";
import { PlaybackSessionBucket } from "./enums";
import type {
  IPlaybackSessionAccumulator,
  IPlaybackSessionAccumulatorSnapshot
} from "./IPlaybackSessionAccumulator";

const TAG = "PlaybackSessionAccumulatorImpl";

/** Max allowed drift between accounted bucket time and wall-clock duration. */
const DRIFT_TOLERANCE_MS = 100;

interface TBucketTally {
  timeMs: number;
  count: number;
}

const createEmptyTallies = (): Map<PlaybackSessionBucket, TBucketTally> => {
  const tallies = new Map<PlaybackSessionBucket, TBucketTally>();
  tallies.set(PlaybackSessionBucket.BUFFERING, { timeMs: 0, count: 0 });
  tallies.set(PlaybackSessionBucket.STALLING, { timeMs: 0, count: 0 });
  tallies.set(PlaybackSessionBucket.PLAYING, { timeMs: 0, count: 0 });
  tallies.set(PlaybackSessionBucket.PAUSED, { timeMs: 0, count: 0 });
  tallies.set(PlaybackSessionBucket.TRICK_PLAY, { timeMs: 0, count: 0 });
  return tallies;
};

export class PlaybackSessionAccumulatorImpl implements IPlaybackSessionAccumulator {
  private mUuidGenerator: () => string;

  private mPlaybackId: string = "";

  private mActive: boolean = false;

  private mHasPlayed: boolean = false;

  private mCurrentBucket: PlaybackSessionBucket = PlaybackSessionBucket.NONE;

  private mBucketEnteredAtMs: number = 0;

  private mSessionStartedAtMs: number = 0;

  private mFirstPlayingElapsedMs: number | null = null;

  private mTallies: Map<PlaybackSessionBucket, TBucketTally> =
    createEmptyTallies();

  public constructor(
    uuidGenerator: () => string = () =>
      CryptoProviderFactory.getProvider().generateUuidV4()
  ) {
    this.mUuidGenerator = uuidGenerator;
  }

  public start(nowMs: number): string {
    this.reset();
    this.mPlaybackId = this.mUuidGenerator();
    this.mActive = true;
    this.mSessionStartedAtMs = nowMs;
    this.mBucketEnteredAtMs = nowMs;
    this.mCurrentBucket = PlaybackSessionBucket.BUFFERING;
    this.incrementCount(PlaybackSessionBucket.BUFFERING);
    return this.mPlaybackId;
  }

  public onStateChanged(bucket: PlaybackSessionBucket, nowMs: number): void {
    if (!this.mActive) {
      return;
    }
    this.accumulateElapsed(nowMs);
    if (bucket === this.mCurrentBucket) {
      return;
    }
    this.mCurrentBucket = bucket;
    this.incrementCount(bucket);
    if (bucket === PlaybackSessionBucket.PLAYING) {
      this.mHasPlayed = true;
      if (this.mFirstPlayingElapsedMs === null) {
        this.mFirstPlayingElapsedMs = nowMs - this.mSessionStartedAtMs;
      }
    }
  }

  public close(nowMs: number): IPlaybackSessionAccumulatorSnapshot {
    this.accumulateElapsed(nowMs);
    const durationMs = Math.max(0, nowMs - this.mSessionStartedAtMs);
    this.reconcileDrift(durationMs);

    const buffering = this.getTally(PlaybackSessionBucket.BUFFERING);
    const stalling = this.getTally(PlaybackSessionBucket.STALLING);
    const playing = this.getTally(PlaybackSessionBucket.PLAYING);
    const paused = this.getTally(PlaybackSessionBucket.PAUSED);
    const trickPlay = this.getTally(PlaybackSessionBucket.TRICK_PLAY);

    const snapshot: IPlaybackSessionAccumulatorSnapshot = {
      playbackId: this.mPlaybackId,
      firstPlayingElapsedMs: this.mFirstPlayingElapsedMs,
      durationMs,
      hasPlayed: this.mHasPlayed,
      bufferingTimeMs: buffering.timeMs,
      bufferingCount: buffering.count,
      stallingTimeMs: stalling.timeMs,
      stallingCount: stalling.count,
      playingTimeMs: playing.timeMs,
      playingCount: playing.count,
      pausingTimeMs: paused.timeMs,
      pausingCount: paused.count,
      trickplayingTimeMs: trickPlay.timeMs,
      trickplayingCount: trickPlay.count
    };

    this.mActive = false;
    return snapshot;
  }

  /**
   * Non-destructive peek — adds the in-progress bucket's elapsed slice to a
   * copy of the tallies without touching `mTallies`/`mBucketEnteredAtMs`, so
   * the session keeps accumulating normally after the read.
   */
  public snapshot(nowMs: number): IPlaybackSessionAccumulatorSnapshot {
    const elapsedInCurrentBucketMs = Math.max(
      0,
      nowMs - this.mBucketEnteredAtMs
    );
    const durationMs = Math.max(0, nowMs - this.mSessionStartedAtMs);

    const withPendingElapsed = (bucket: PlaybackSessionBucket): number => {
      const timeMs = this.getTally(bucket).timeMs;
      return bucket === this.mCurrentBucket
        ? timeMs + elapsedInCurrentBucketMs
        : timeMs;
    };

    return {
      playbackId: this.mPlaybackId,
      firstPlayingElapsedMs: this.mFirstPlayingElapsedMs,
      durationMs,
      hasPlayed: this.mHasPlayed,
      bufferingTimeMs: withPendingElapsed(PlaybackSessionBucket.BUFFERING),
      bufferingCount: this.getTally(PlaybackSessionBucket.BUFFERING).count,
      stallingTimeMs: withPendingElapsed(PlaybackSessionBucket.STALLING),
      stallingCount: this.getTally(PlaybackSessionBucket.STALLING).count,
      playingTimeMs: withPendingElapsed(PlaybackSessionBucket.PLAYING),
      playingCount: this.getTally(PlaybackSessionBucket.PLAYING).count,
      pausingTimeMs: withPendingElapsed(PlaybackSessionBucket.PAUSED),
      pausingCount: this.getTally(PlaybackSessionBucket.PAUSED).count,
      trickplayingTimeMs: withPendingElapsed(PlaybackSessionBucket.TRICK_PLAY),
      trickplayingCount: this.getTally(PlaybackSessionBucket.TRICK_PLAY).count
    };
  }

  public reset(): void {
    this.mPlaybackId = "";
    this.mActive = false;
    this.mHasPlayed = false;
    this.mCurrentBucket = PlaybackSessionBucket.NONE;
    this.mBucketEnteredAtMs = 0;
    this.mSessionStartedAtMs = 0;
    this.mFirstPlayingElapsedMs = null;
    this.mTallies = createEmptyTallies();
  }

  public isActive(): boolean {
    return this.mActive;
  }

  public getPlaybackId(): string {
    return this.mPlaybackId;
  }

  public hasPlayed(): boolean {
    return this.mHasPlayed;
  }

  private getTally(bucket: PlaybackSessionBucket): TBucketTally {
    const tally = this.mTallies.get(bucket);
    return tally !== undefined ? tally : { timeMs: 0, count: 0 };
  }

  private incrementCount(bucket: PlaybackSessionBucket): void {
    const tally = this.mTallies.get(bucket);
    if (tally !== undefined) {
      tally.count += 1;
    }
  }

  private accumulateElapsed(nowMs: number): void {
    const elapsed = Math.max(0, nowMs - this.mBucketEnteredAtMs);
    const tally = this.mTallies.get(this.mCurrentBucket);
    if (tally !== undefined) {
      tally.timeMs += elapsed;
    }
    this.mBucketEnteredAtMs = nowMs;
  }

  /**
   * Snaps the largest bucket to absorb any drift between accounted bucket
   * time and total wall-clock duration, keeping the sum-of-buckets ==
   * duration invariant exact once drift exceeds the 100ms tolerance.
   */
  private reconcileDrift(durationMs: number): void {
    let totalAccountedMs = 0;
    let largestBucket: PlaybackSessionBucket | null = null;
    let largestTimeMs = -1;
    for (const [bucket, tally] of this.mTallies) {
      totalAccountedMs += tally.timeMs;
      if (tally.timeMs > largestTimeMs) {
        largestTimeMs = tally.timeMs;
        largestBucket = bucket;
      }
    }

    const drift = durationMs - totalAccountedMs;
    if (Math.abs(drift) <= DRIFT_TOLERANCE_MS || largestBucket === null) {
      return;
    }

    Log.w(
      TAG,
      "reconcileDrift() - drift=" + drift + "ms, snapping " + largestBucket
    );
    const tally = this.mTallies.get(largestBucket);
    if (tally !== undefined) {
      tally.timeMs = Math.max(0, tally.timeMs + drift);
    }
  }
}
